// Currency conversion rates (base: USD)
// All rates are relative to 1 USD
const USD_BASE_RATES = {
  USD: 1.0,
  MAD: 10.0,      // 1 USD = 10.0 MAD
  EUR: 0.93,      // 1 USD = 0.93 EUR
  GBP: 0.79,      // 1 USD = 0.79 GBP
};

export type Currency = "MAD" | "USD" | "EUR" | "GBP";

/**
 * Converts an amount from one currency to another using USD as base
 * @param amount - The amount to convert
 * @param fromCurrency - The source currency
 * @param toCurrency - The target currency
 * @returns The converted amount rounded to 2 decimal places
 */
export function convertCurrency(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency
): number {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  // Convert from source currency to USD first
  const amountInUSD = amount / USD_BASE_RATES[fromCurrency];
  
  // Then convert from USD to target currency
  const convertedAmount = amountInUSD * USD_BASE_RATES[toCurrency];

  return Math.round(convertedAmount * 100) / 100;
}

/**
 * Formats a currency amount with the appropriate symbol
 * @param amount - The amount to format
 * @param currency - The currency code
 * @returns Formatted string with currency symbol
 */
export function formatCurrency(amount: number, currency: Currency): string {
  const symbols: Record<Currency, string> = {
    MAD: "MAD",
    USD: "$",
    EUR: "€",
    GBP: "£",
  };

  const formattedAmount = amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return currency === "MAD"
    ? `${formattedAmount} ${symbols[currency]}`
    : `${symbols[currency]}${formattedAmount}`;
}

/**
 * Get conversion info text for display
 * @param originalAmount - Original amount in source currency
 * @param sourceCurrency - Source currency (defaults to USD)
 * @param targetCurrency - Target currency to convert to
 * @returns Formatted conversion info string
 */
export function getConversionInfo(
  originalAmount: number,
  targetCurrency: Currency,
  sourceCurrency: Currency = "USD"
): string {
  if (targetCurrency === sourceCurrency) {
    return "";
  }

  const convertedAmount = convertCurrency(originalAmount, sourceCurrency, targetCurrency);
  return `(${formatCurrency(originalAmount, sourceCurrency)} = ${formatCurrency(convertedAmount, targetCurrency)})`;
}
